import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { jobPhotoPath } from '@/lib/checklists';
import { compressImage } from '@/lib/image-compress';

// Job photos, completion and completion reports (migration
// 20261102005644_job_checklists_photos_reports.sql). Photos live in the
// PRIVATE job-photos bucket and are only ever shown through signed URLs.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export const JOB_PHOTOS_BUCKET = 'job-photos';
/** Signed URLs for the job dialog last an hour; the query refreshes well before. */
const SIGNED_URL_SECONDS = 60 * 60;

export type PhotoKind = 'before' | 'after';

export interface JobPhoto {
  id: string;
  job_id: string;
  storage_path: string;
  kind: PhotoKind;
  caption: string | null;
  uploaded_by: string | null;
  created_at: string;
  url: string | null;
}

export const jobPhotosKey = (jobId: string | null | undefined) => ['job-photos', jobId] as const;

export function useJobPhotos(jobId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: jobPhotosKey(jobId),
    enabled: Boolean(jobId) && enabled,
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<JobPhoto[]> => {
      const { data, error } = await db
        .from('job_photos')
        .select('id, job_id, storage_path, kind, caption, uploaded_by, created_at')
        .eq('job_id', jobId)
        .order('created_at');
      if (error) throw error;
      const rows = (data ?? []) as Omit<JobPhoto, 'url'>[];
      if (rows.length === 0) return [];
      const { data: signed } = await supabase.storage
        .from(JOB_PHOTOS_BUCKET)
        .createSignedUrls(rows.map((r) => r.storage_path), SIGNED_URL_SECONDS);
      const byPath = new Map((signed ?? []).map((s) => [s.path, s.signedUrl] as const));
      return rows.map((row) => ({ ...row, url: byPath.get(row.storage_path) ?? null }));
    },
  });
}

export function useUploadJobPhoto(orgId: string | null | undefined, jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, kind, caption }: { file: File; kind: PhotoKind; caption?: string }) => {
      if (!orgId) throw new Error('No business selected');
      const { data: auth } = await supabase.auth.getUser();
      const compressed = await compressImage(file);
      const path = jobPhotoPath(orgId, jobId, crypto.randomUUID());
      const { error: uploadError } = await supabase.storage
        .from(JOB_PHOTOS_BUCKET)
        .upload(path, compressed.blob, { contentType: 'image/jpeg', upsert: false });
      if (uploadError) throw uploadError;
      const { error } = await db.from('job_photos').insert({
        job_id: jobId,
        organization_id: orgId,
        storage_path: path,
        kind,
        caption: caption?.trim() || null,
        width: compressed.width,
        height: compressed.height,
        size_bytes: compressed.blob.size,
        uploaded_by: auth?.user?.id ?? undefined,
      });
      if (error) {
        // Don't leave an orphaned file behind.
        await supabase.storage.from(JOB_PHOTOS_BUCKET).remove([path]);
        throw error;
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: jobPhotosKey(jobId) }),
  });
}

export function useDeleteJobPhoto(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (photo: Pick<JobPhoto, 'id' | 'storage_path'>) => {
      const { error } = await db.from('job_photos').delete().eq('id', photo.id);
      if (error) throw error;
      await supabase.storage.from(JOB_PHOTOS_BUCKET).remove([photo.storage_path]);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: jobPhotosKey(jobId) }),
  });
}

// --- Completion ---------------------------------------------------------------

export interface JobCompletion {
  status: string;
  completion_note: string | null;
  completed_at: string | null;
}

export const jobCompletionKey = (jobId: string | null | undefined) => ['job-completion', jobId] as const;

/** The job's live status + completion note (the dialogs' job objects can be stale). */
export function useJobCompletion(jobId: string | null | undefined) {
  return useQuery({
    queryKey: jobCompletionKey(jobId),
    enabled: Boolean(jobId),
    queryFn: async (): Promise<JobCompletion | null> => {
      const { data, error } = await db
        .from('jobs')
        .select('status, completion_note, completed_at')
        .eq('id', jobId)
        .maybeSingle();
      if (error) return null;
      return (data as JobCompletion | null) ?? null;
    },
  });
}

export function useCompleteJobVisit(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (note: string) => {
      const { error } = await db.rpc('complete_job_visit', { _job_id: jobId, _note: note });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobCompletionKey(jobId) });
      queryClient.invalidateQueries({ queryKey: jobReportsKey(jobId) });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// --- Completion reports --------------------------------------------------------

export interface JobReportSettings {
  auto_send_on_complete: boolean;
  include_photos: boolean;
}

export const DEFAULT_REPORT_SETTINGS: JobReportSettings = { auto_send_on_complete: false, include_photos: true };

export function useJobReportSettings(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['job-report-settings', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<JobReportSettings> => {
      const { data, error } = await db
        .from('org_job_report_settings')
        .select('auto_send_on_complete, include_photos')
        .eq('organization_id', orgId)
        .maybeSingle();
      if (error || !data) return DEFAULT_REPORT_SETTINGS;
      return data as JobReportSettings;
    },
  });
}

export function useSaveJobReportSettings(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: JobReportSettings) => {
      const { error } = await db
        .from('org_job_report_settings')
        .upsert({ organization_id: orgId, ...settings }, { onConflict: 'organization_id' });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['job-report-settings', orgId] }),
  });
}

export interface JobCompletionReport {
  id: string;
  trigger: 'manual' | 'auto';
  status: 'sending' | 'sent' | 'failed';
  recipient: string | null;
  created_at: string;
  sent_at: string | null;
}

export const jobReportsKey = (jobId: string | null | undefined) => ['job-completion-reports', jobId] as const;

export function useJobCompletionReports(jobId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: jobReportsKey(jobId),
    enabled: Boolean(jobId) && enabled,
    queryFn: async (): Promise<JobCompletionReport[]> => {
      const { data, error } = await db
        .from('job_completion_reports')
        .select('id, trigger, status, recipient, created_at, sent_at')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) return [];
      return (data ?? []) as JobCompletionReport[];
    },
  });
}

export function useSendCompletionReport(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ recipient?: string }> => {
      const { data, error } = await supabase.functions.invoke('send-completion-report', {
        body: { jobId, trigger: 'manual' },
      });
      if (error) {
        // Surface the function's own message ("This job has no customer email") when there is one.
        const context = (error as { context?: Response }).context;
        if (context && typeof context.json === 'function') {
          const body = await context.json().catch(() => null);
          if (body?.error) throw new Error(body.error);
        }
        throw error;
      }
      if (data?.error) throw new Error(data.error);
      return data ?? {};
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: jobReportsKey(jobId) }),
  });
}
